import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Repeat, 
  Wand2,
  Twitter, 
  Instagram, 
  Facebook, 
  Linkedin,
  MessageSquare,
  Copy,
  Sparkles,
  AlertCircle,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { streamOpenAIResponse } from '../lib/openai';

interface RepurposedContent {
  platform: string;
  content: string;
}

type Mode = 'variations' | 'multi-post';

const Repurpose = () => {
  const [originalContent, setOriginalContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [repurposedContent, setRepurposedContent] = useState<RepurposedContent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('variations');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);

  const handleGenerate = async (mode: Mode) => {
    if (!originalContent.trim()) {
      setError('Please enter some content to repurpose');
      return;
    }

    if (mode === 'multi-post' && selectedPlatforms.length === 0) {
      setError('Please select at least one platform');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setRepurposedContent([]);

    try {
      let prompt = '';
      if (mode === 'variations') {
        prompt = `Generate 4 different variations of this content, keeping the same message but with different writing styles and approaches:\n\n${originalContent}`;
      } else {
        prompt = `Adapt this content for the following platforms, optimizing for each platform's best practices and character limits. Format your response with clear platform headers followed by the content for that platform. Use this exact format for each platform:

[Platform Name]:
[Platform-specific content]

Platforms to adapt for:
${selectedPlatforms.map(p => `- ${p.charAt(0).toUpperCase() + p.slice(1)}`).join('\n')}

Content to adapt:
${originalContent}`;
      }

      let generatedContent = '';
      let platformContent: Record<string, string> = {};
      let currentPlatform = '';

      const updatePlatformContent = () => {
        if (currentPlatform && platformContent[currentPlatform]) {
          platformContent[currentPlatform] = platformContent[currentPlatform].trim();
        }
      };

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          generatedContent += chunk;
          
          if (mode === 'variations') {
            // Parse variations
            const variations = generatedContent
              .split(/\d+\.\s+/g)
              .filter(Boolean)
              .map(content => content.trim());
            
            setRepurposedContent(
              variations.map(content => ({
                platform: 'variation',
                content: content.trim()
              }))
            );
          } else {
            // Parse platform-specific content
            const lines = generatedContent.split('\n');
            let currentPlatform = '';
            
            for (const line of lines) {
              const platformMatch = line.match(/^([A-Za-z]+):/);
              
              if (platformMatch) {
                currentPlatform = platformMatch[1].toLowerCase();
                platformContent[currentPlatform] = '';
              } else if (currentPlatform && selectedPlatforms.includes(currentPlatform)) {
                platformContent[currentPlatform] += (platformContent[currentPlatform] ? '\n' : '') + line;
              }
            }
            
            // Update repurposed content state with platform-specific content
            setRepurposedContent(
              selectedPlatforms.map(platform => ({
                platform,
                content: platformContent[platform]?.trim() || ''
              }))
            );
          }
        },
        (error) => {
          setError(error.message);
        }
      );
    } catch (err) {
      setError('Failed to generate content. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'twitter':
        return <Twitter className="h-5 w-5 text-[#1DA1F2]" />;
      case 'instagram':
        return <Instagram className="h-5 w-5 text-[#E4405F]" />;
      case 'facebook':
        return <Facebook className="h-5 w-5 text-[#1877F2]" />;
      case 'linkedin':
        return <Linkedin className="h-5 w-5 text-[#0A66C2]" />;
      case 'variation':
        return <Wand2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />;
      default:
        return <MessageSquare className="h-5 w-5 text-gray-400" />;
    }
  };

  const handleCopy = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      // You could add a toast notification here
    } catch (err) {
      setError('Failed to copy content to clipboard');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Content Repurposing</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Transform your long-form content into platform-specific formats
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6 space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Original Content</h2>
            <textarea
              value={originalContent}
              onChange={(e) => setOriginalContent(e.target.value)}
              placeholder="Paste your long-form content here..."
              className="w-full h-48 p-4 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent resize-none"
            />
            
            <div className="flex flex-col space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Choose Repurposing Mode</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setMode('variations')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    mode === 'variations'
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-dark-700'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                      <Wand2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <span className="font-medium">Generate Variations</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Create different versions of your content with varied tones and styles
                  </p>
                </button>
                
                <button
                  onClick={() => setMode('multi-post')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    mode === 'multi-post'
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-dark-700'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                      <Repeat className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <span className="font-medium">Multi-Platform Post</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Adapt your content for different social media platforms
                  </p>
                </button>
              </div>
              
              {mode === 'multi-post' && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Platforms</h3>
                  <div className="flex flex-wrap gap-2">
                    {['twitter', 'instagram', 'facebook', 'linkedin'].map(platform => (
                      <button
                        key={platform}
                        onClick={() => setSelectedPlatforms(prev =>
                          prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
                        )}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                          selectedPlatforms.includes(platform)
                            ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                            : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {getPlatformIcon(platform)}
                        <span className="capitalize">{platform}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleGenerate(mode)}
                disabled={isGenerating || (mode === 'multi-post' && selectedPlatforms.length === 0)}
                className={`flex items-center justify-center px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors ${
                  (isGenerating || (mode === 'multi-post' && selectedPlatforms.length === 0))
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    {mode === 'variations' ? 'Generate Variations' : 'Generate Platform Posts'}
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </div>

        {/* Output Section */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              {mode === 'variations' ? 'Content Variations' : 'Platform-Specific Content'}
              {repurposedContent.length > 0 && (
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                  {repurposedContent.length} {mode === 'variations' ? 'variations' : 'posts'} generated
                </span>
              )}
            </h2>
            <div className="space-y-4">
              {repurposedContent.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <Repeat className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>
                    {mode === 'variations'
                      ? 'Content variations will appear here'
                      : 'Platform-specific posts will appear here'}
                  </p>
                </div>
              ) : (
                repurposedContent.map((content, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {getPlatformIcon(content.platform)}
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {content.platform === 'variation' 
                            ? `Variation ${index + 1}` 
                            : content.platform.charAt(0).toUpperCase() + content.platform.slice(1)
                          }
                        </span>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleCopy(content.content)}
                        className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                      >
                        <Copy className="h-4 w-4" />
                      </motion.button>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
                      {content.content}
                    </p>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Tips Section */}
      <div className="mt-8 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Sparkles className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">Pro Tips</h3>
        </div>
        <ul className="space-y-3 text-indigo-900 dark:text-indigo-100">
          <li className="flex items-center space-x-2">
            <ArrowRight className="h-4 w-4" />
            <span>For best results, provide detailed context in your original content</span>
          </li>
          <li className="flex items-center space-x-2">
            <ArrowRight className="h-4 w-4" />
            <span>Use the variations mode to experiment with different tones and styles</span>
          </li>
          <li className="flex items-center space-x-2">
            <ArrowRight className="h-4 w-4" />
            <span>Multi-platform mode automatically optimizes for each platform's requirements</span>
          </li>
        </ul>
      </div>
    </motion.div>
  );
};

export default Repurpose;